import React, {
    useEffect
} from 'react';

import PropTypes from 'prop-types';

import {
    LoadingStatus,
    useSetInstanceLoadingStatus
} from '../hooks/loadingStatusHooks.js';

import useMetrics from '../hooks/useMetrics.js';
import useCDNUrl from '../hooks/useCDNUrl.js';

MetricLoadingView.propTypes = {
    instanceId: PropTypes.string.isRequired,
    metricRequests: PropTypes.object,
    children: PropTypes.func.isRequired,
    emptyFallback: PropTypes.node,
    fallback: PropTypes.node
};

export function Loading() {
    return (
        <div className='tlw-flex tlw-h-full tlw-min-h-[100px] tlw-grow tlw-items-center tlw-justify-center tlw-text-[36px] tlw-text-gray-500'>
            <span className='fa fa-circle-o-notch fa-spin'></span>
        </div>
    );
}

export function Failed() {
    const sadGraphsUrl = useCDNUrl('/images/sad_graphs.png');
    return (
        <div className='tlw-my-10 tlw-flex tlw-flex-col tlw-items-center'>
            <img
                alt='error'
                className='tlw-m-auto tlw-max-h-[96px]'
                src={sadGraphsUrl}
            />
        </div>
    );
}

// Convenience view if you have a widget that should show a loading spinner while metrics are loading, then display results when the data comes in. This
// is the spiritual successor to the marionette BlockLoading views.
export default function MetricLoadingView({children, emptyFallback, fallback, instanceId, metricRequests}) {
    const updateLoadingStatus = useSetInstanceLoadingStatus(instanceId);
    const {isLoading, error, data: metricResults} = useMetrics(metricRequests);

    let newLoadingStatus = LoadingStatus.COMPLETE;
    let loadingError;
    if (isLoading) {
        newLoadingStatus = LoadingStatus.LOADING;
    } else if (error) {
        newLoadingStatus = LoadingStatus.FAILED;
        loadingError = error.message;
    } else {
        const erroredMetricRequest = _.find(metricResults, metricResult => metricResult.error);
        if (erroredMetricRequest) {
            newLoadingStatus = LoadingStatus.FAILED;
            loadingError = erroredMetricRequest.error.message;
        }
    }

    useEffect(() => {
        updateLoadingStatus(newLoadingStatus, loadingError);
    }, [updateLoadingStatus, newLoadingStatus, loadingError]);

    if (newLoadingStatus === LoadingStatus.LOADING) {
        if (fallback) {
            return fallback;
        }
        return <Loading/>;
    }

    if (newLoadingStatus === LoadingStatus.FAILED) {
        return <Failed/>;
    }

    if (newLoadingStatus === LoadingStatus.COMPLETE) {
        if (emptyFallback && _.every(metricResults, ({data}) => _.isEmpty(data))) {
            return emptyFallback;
        }

        return children(metricResults);
    }

    return null;
}
